import { Box, Group, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { Breadcrumbs, Button, IconButton, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import type { RightSidebarStore } from '../../components/RightSidebar/store';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarSidesheetControlsWrapper,
	NavBarWrapper,
} from '../../components/SecodaAppShell/SecodaNavBar';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { ActionMenu } from './components/ActionMenu';
import { AddToDashboardMenu } from './components/AddToDashboardMenu';
import { ExportMenu } from './components/ExportMenu';
import { useBiMetricBreadcrumbs } from './hooks/useBiMetricBreadcrumbs';
import { useBiMetricExport } from './hooks/useBiMetricExport';
import type { BIMetricStore } from './store';

interface BIMetricNavbarControllerProps {
	store: BIMetricStore;
	rightSidebarStore: RightSidebarStore;
	closeCanvas?: () => void;
	navigateToDashboardInCanvas?: (id: string) => void;
	mode: 'canvas' | 'page';
	selectedSidebarType: 'ai' | 'info';
	setSelectedSidebarType: (type: 'ai' | 'info') => void;
}

export const BiMetricNavbarController = observer(
	({
		store,
		rightSidebarStore,
		closeCanvas,
		mode,
		navigateToDashboardInCanvas,
		selectedSidebarType,
		setSelectedSidebarType,
	}: BIMetricNavbarControllerProps) => {
		const { collapsed, setCollapsed } = rightSidebarStore;
		const navigate = useNavigate();

		const [
			isLineageModalOpen,
			{ open: openLineageModal, close: closeLineageModal },
		] = useDisclosure(false);
		const { breadcrumbs, dashboard } = useBiMetricBreadcrumbs({
			metricId: store.metricId,
			entity: store.originalMetric.secoda_entity,
		});
		const {
			handleCopyAsPng: handleCopyAsPng,
			handleDownloadPng: handleDownloadPng,
		} = useBiMetricExport({
			biMetricId: store.metricVersion.bi_metric_id,
			metricName: store.metricVersion.name,
		});

		const { hasPermission: hasManagePermission } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Dashboards.Manage',
		});

		const onDelete = useCallback(() => {
			// Navigate back to previous page or metrics list
			navigate(-1);
		}, [navigate]);

		// Common sidebar controls - used in both modes
		const sidebarControls = useMemo(
			() =>
				hasManagePermission ? (
					<NavBarSidesheetControlsWrapper store={rightSidebarStore}>
						<IconButton
							iconName="sparkles"
							highlight={!collapsed && selectedSidebarType === 'ai'}
							variant="tertiary"
							tooltip="Show AI sidebar"
							onClick={() => {
								if (collapsed) {
									setCollapsed(false);
									setSelectedSidebarType('ai');
								} else {
									if (selectedSidebarType !== 'ai') {
										setSelectedSidebarType('ai');
									} else {
										setCollapsed(true);
									}
								}
							}}
						/>
						<IconButton
							iconName="adjustmentsHorizontal"
							highlight={!collapsed && selectedSidebarType === 'info'}
							variant="tertiary"
							tooltip="Edit metric"
							onClick={() => {
								if (collapsed) {
									setCollapsed(false);
									setSelectedSidebarType('info');
								} else {
									if (selectedSidebarType !== 'info') {
										setSelectedSidebarType('info');
									} else {
										setCollapsed(true);
									}
								}
							}}
						/>
					</NavBarSidesheetControlsWrapper>
				) : null,
			[
				hasManagePermission,
				rightSidebarStore,
				collapsed,
				setCollapsed,
				selectedSidebarType,
				setSelectedSidebarType,
			]
		);

		// Canvas mode content
		const canvasNavBarContent = useMemo(
			() => ({
				left: (
					<NavBarBreadcrumbWrapper>
						<Group spacing="xs" align="center" noWrap>
							<IconButton
								iconName="chevronsRight"
								variant="tertiary"
								tooltip="Back"
								onClick={() => {
									closeCanvas?.();
								}}
								size="md"
							/>
							{dashboard && (
								<>
									<Button
										size="md"
										variant="tertiary"
										onClick={() => {
											navigateToDashboardInCanvas?.(dashboard.id);
										}}
									>
										{dashboard.secoda_entity.title}
									</Button>
									<Text
										size="xs"
										weight="semibold"
										color="text/primary/default"
										lineClamp={1}
									>
										{`/`}
									</Text>
								</>
							)}
							<Text size="xs" weight="semibold" color="text/primary/default">
								{store.metricVersion.name}
							</Text>
						</Group>
					</NavBarBreadcrumbWrapper>
				),
				right: (
					<NavBarRightSideWrapper>
						<Group spacing="xs">
							{hasManagePermission && (
								<AddToDashboardMenu store={store} size="md" />
							)}
						</Group>
					</NavBarRightSideWrapper>
				),
			}),
			[
				dashboard,
				store,
				closeCanvas,
				navigateToDashboardInCanvas,
				hasManagePermission,
			]
		);

		// Page mode content
		const pageNavBarContent = useMemo(
			() => ({
				left: (
					<NavBarBreadcrumbWrapper>
						<Breadcrumbs
							crumbs={breadcrumbs}
							data-testid="bi-metric-breadcrumbs"
						/>
					</NavBarBreadcrumbWrapper>
				),
				right: (
					<NavBarRightSideWrapper>
						<Group spacing="xs">
							{hasManagePermission && (
								<AddToDashboardMenu store={store} size="md" />
							)}
							<ExportMenu
								handleCopyPng={handleCopyAsPng}
								handleDownloadPng={handleDownloadPng}
							/>
							<ActionMenu
								openLineageModal={openLineageModal}
								metricName={store.metricVersion.name}
								metricId={store.metricId}
								numDashboards={store.originalMetric.dashboards.length}
								onDelete={onDelete}
							/>
						</Group>
					</NavBarRightSideWrapper>
				),
			}),
			[
				breadcrumbs,
				store,
				handleCopyAsPng,
				handleDownloadPng,
				openLineageModal,
				onDelete,
				hasManagePermission,
			]
		);

		const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
			useLineageTab(
				store.originalMetric.secoda_entity.id,
				store.originalMetric.secoda_entity.published,
				EntityType.bi_metric,
				store.originalMetric.secoda_entity.native_type
			);

		// Select content based on mode
		const content = mode === 'canvas' ? canvasNavBarContent : pageNavBarContent;

		return (
			<NavBarWrapper>
				{content.left}
				{content.right}
				{sidebarControls}
				<Modal
					opened={isLineageModalOpen}
					onClose={closeLineageModal}
					title="Lineage"
					size="80vw"
					withinPortal
					keepMounted
				>
					<Box h="80vh">
						<LineageTab
							lineageFlowStore={lineageFlowStore}
							lineageFlowUIStore={lineageFlowUIStore}
							lineageFilterStore={lineageFilterStore}
						/>
					</Box>
				</Modal>
			</NavBarWrapper>
		);
	}
);
